// Weather (docs/20 W1–W4). An encounter has a weather — clear, overcast, rain, storm, fog or snow —
// with an intensity and a wind. Like the hour it is a pure function of the room (its own `weather`
// when the forge stamps one, else a hash of the encounter id), so a party of four sees one sky.
//
// Weather never touches the rules; it only reshapes the hour's light (`weathered`) and says what the
// ground shader and the particles should draw (`weatherLook`). The legibility floors of docs/19 N2
// still hold: the weathered ambient goes through `legible()`, the shader may only darken the ground
// as far as the floors allow (`darkBudget`), fog and snow are capped, and lightning is capped and rare.
import { cellHash, weightedIndex } from '../tiling/hash';
import { AMBIENT_FLOOR, LUMA_FLOOR, legible, luma, mix, type DayLight, type RGB } from '../lighting/daylight';

export type WeatherKind = 'clear' | 'overcast' | 'rain' | 'storm' | 'fog' | 'snow' | 'heat' | 'sandstorm' | 'blizzard' | 'frost' | 'sleet';
export const WEATHER_KINDS: readonly WeatherKind[] = ['clear', 'overcast', 'rain', 'storm', 'fog', 'snow', 'heat', 'sandstorm', 'blizzard', 'frost', 'sleet'];

export interface Weather {
  kind: WeatherKind;
  /** 0..1; rain below ~0.55 reads as drizzle, snow below it as flurries. */
  intensity: number;
  /** Wind across the board, −1 (blowing west) .. 1 (blowing east). Rain slants and fog drifts with it. */
  wind: number;
}

/** Fog never washes the ground more than this toward its colour; marks and units sit above it anyway. */
export const FOG_CAP = 0.5;
/** Snow never covers the ground more than this (the tiles' detail always shows through a little). */
export const SNOW_CAP = 0.82;
/** A lightning flash adds at most this much light, and lasts well under half a second. */
export const FLASH_CAP = 0.32;
/** Flashes are at least this far apart (ms) — far below the 3-per-second photosensitivity line. */
export const FLASH_GAP_MS = 4000;
/**
 * A puddle is a material, like the river tiles, so it is the one thing allowed under the floors: it
 * darkens its own pixels (never a whole square — the noise keeps them small) by at most this much.
 */
export const PUDDLE_DARK = 0.28;
/** Rime (frost) never whitens a pixel more than this; it is a sparse crystal dither, never a wash. */
export const FROST_CAP = 0.6;
/** Heat shimmer brightens or bleaches the ground by at most this much (it only ever lightens). */
export const HEAT_CAP = 0.14;
/** Sand drifts cover open ground at most this much (lighter than snow: the dunes' own sand shows). */
export const SAND_CAP = 0.5;
/** Cloud shadows and wet ground darken by at most this much, before the floor budget. */
const DARK_CAP = 0.24;

/**
 * How likely each weather is per region kit (docs/20 W2). Indoors the weather is what is happening
 * outside: it shows at the exits (rain, lightning, grey daylight) and in the drips.
 */
export const WEATHER_ODDS: Record<string, Partial<Record<WeatherKind, number>>> = {
  forest: { clear: 5, overcast: 3, rain: 3, storm: 1, fog: 2, snow: 2 },
  autumn: { clear: 4, overcast: 3, rain: 3, storm: 1, fog: 3, snow: 1 },
  mirewood: { clear: 2, overcast: 3, rain: 3, storm: 2, fog: 5 },
  // docs/23 biome kits (the module rolls from its own biome × season table; these are the fallback)
  winterwood: { snow: 5, frost: 4, clear: 3, overcast: 3, blizzard: 2, fog: 1, sleet: 1 },
  pinewood: { clear: 4, overcast: 3, rain: 3, fog: 3, storm: 1, snow: 1, sleet: 1, frost: 1 },
  dunes: { clear: 5, heat: 5, sandstorm: 3, overcast: 1, rain: 1 },
  highland: { clear: 3, overcast: 4, rain: 3, fog: 3, storm: 2, sleet: 1, snow: 1, frost: 1 },
  jungle: { clear: 3, heat: 3, rain: 5, storm: 3, fog: 2, overcast: 1 },
  // docs/23 Y9: under the rock rain is the seep, fog spore haze, frost the rime of the deep still
  underdark: { fog: 4, rain: 3, clear: 3, overcast: 2, heat: 1, frost: 1 },
  // docs/23 Y10: the Ashlands' rain is black, its fog the sulphur reek, its sandstorm an ash storm
  ashlands: { heat: 3, overcast: 3, sandstorm: 3, fog: 2, rain: 2, storm: 2, clear: 1, frost: 1 },
  indoor: { clear: 6, overcast: 2, rain: 3, storm: 2, fog: 1 },
};

export function oddsFor(kitKey: string, setting: 'indoor' | 'outdoor'): Partial<Record<WeatherKind, number>> {
  return WEATHER_ODDS[kitKey] ?? (setting === 'indoor' ? WEATHER_ODDS['indoor']! : WEATHER_ODDS['forest']!);
}

const WIND: Record<WeatherKind, readonly [number, number]> = {
  clear: [0.1, 0.3], overcast: [0.2, 0.5], rain: [0.25, 0.6], storm: [0.7, 1], fog: [0.04, 0.12], snow: [0.15, 0.45],
  heat: [0.04, 0.16], sandstorm: [0.65, 1], blizzard: [0.7, 1], frost: [0, 0.08], sleet: [0.3, 0.65],
};

/** Parse `rain`, `rain:0.4` or `rain:0.4:-0.8` (kind, intensity, wind) — the gallery and `?weather=` use it. */
export function parseWeather(s: string | undefined | null): Weather | undefined {
  if (!s) return undefined;
  const [k, i, w] = s.split(':');
  if (!WEATHER_KINDS.includes(k as WeatherKind)) return undefined;
  const kind = k as WeatherKind;
  const intensity = i !== undefined && Number.isFinite(Number(i)) ? Math.max(0, Math.min(1, Number(i))) : 0.8;
  const [lo, hi] = WIND[kind];
  const wind = w !== undefined && Number.isFinite(Number(w)) ? Math.max(-1, Math.min(1, Number(w))) : (lo + hi) / 2;
  return { kind, intensity, wind };
}

/**
 * The encounter's weather (W1): the room's own when the forge stamps one; otherwise a stable hash of
 * the encounter id (every client in the party agrees), else of the board seed — weighted by the kit's odds.
 */
export function encounterWeather(opts: { weather?: string; encounterId?: bigint | number; seed?: number }, kitKey: string, setting: 'indoor' | 'outdoor'): Weather {
  const stamped = parseWeather(opts.weather);
  if (stamped) return stamped;
  const key = opts.encounterId !== undefined ? `enc:${String(opts.encounterId)}` : `seed:${opts.seed ?? 0}`;
  const odds = oddsFor(kitKey, setting);
  const kinds = WEATHER_KINDS.filter((k) => (odds[k] ?? 0) > 0);
  const kind = kinds[weightedIndex(kinds.map((k) => odds[k]!), cellHash(key, 0, 0, 'weather'))]!;
  const u = (p: string): number => (cellHash(key, 0, 0, p) % 1000) / 999;
  const [lo, hi] = WIND[kind];
  const intensity = 0.4 + 0.6 * u('weather-intensity');
  const wind = (lo + (hi - lo) * u('wind')) * (u('wind-dir') < 0.5 ? -1 : 1);
  return { kind, intensity, wind };
}

export function weatherLabel(w: Weather): string {
  switch (w.kind) {
    case 'clear': return 'Clear';
    case 'overcast': return 'Overcast';
    case 'rain': return w.intensity < 0.55 ? 'Drizzle' : 'Rain';
    case 'storm': return 'Thunderstorm';
    case 'fog': return w.intensity < 0.55 ? 'Mist' : 'Fog';
    case 'snow': return w.intensity < 0.55 ? 'Flurries' : 'Snow';
    case 'heat': return w.intensity < 0.55 ? 'Heat haze' : 'Scorching';
    case 'sandstorm': return w.intensity < 0.55 ? 'Dust' : 'Sandstorm';
    case 'blizzard': return 'Blizzard';
    case 'frost': return w.intensity < 0.55 ? 'Frost' : 'Hard frost';
    case 'sleet': return 'Sleet';
  }
}

/** What a weather does to the hour's light, at full intensity (W3). */
interface Shift {
  /** Multiplies the outdoor ambient (floors applied after). */
  ambient: RGB;
  /** Multiplies sun/moon shadow strength — cloud cover makes the light diffuse. */
  shadow: number;
  /** Multiplies daylight through exits, and greys it. */
  sky: number;
  saturation: number; warmth: number; vignette: number;
  /** What the sky greys toward (default GREY); sand storms turn it ochre. */
  skyTo?: RGB;
  /** Grade extras: contrast offset, exposure multiplier, shadow length multiplier. */
  contrast?: number; exposure?: number; length?: number;
}

const SHIFT: Record<WeatherKind, Shift> = {
  clear: { ambient: [1, 1, 1], shadow: 1, sky: 1, saturation: 0, warmth: 0, vignette: 0 },
  overcast: { ambient: [0.86, 0.88, 0.94], shadow: 0.35, sky: 0.8, saturation: -0.14, warmth: -0.12, vignette: 0.04 },
  rain: { ambient: [0.78, 0.82, 0.9], shadow: 0.2, sky: 0.65, saturation: -0.2, warmth: -0.22, vignette: 0.08 },
  storm: { ambient: [0.66, 0.7, 0.84], shadow: 0.08, sky: 0.5, saturation: -0.28, warmth: -0.3, vignette: 0.14 },
  fog: { ambient: [0.92, 0.93, 0.96], shadow: 0.25, sky: 0.75, saturation: -0.3, warmth: -0.1, vignette: 0.06 },
  snow: { ambient: [0.95, 0.98, 1.0], shadow: 0.45, sky: 0.85, saturation: -0.22, warmth: -0.35, vignette: 0.04 },
  // docs/23: bleached, warm, hard light and short dark shadows
  heat: { ambient: [1.0, 0.98, 0.92], shadow: 1.3, sky: 1, saturation: -0.16, warmth: 0.34, vignette: -0.04, skyTo: [1.0, 0.94, 0.8], contrast: 0.12, exposure: 1.05, length: 0.7 },
  // an ochre gloom driven by the wind; almost no shadow
  sandstorm: { ambient: [0.92, 0.84, 0.7], shadow: 0.12, sky: 0.55, saturation: -0.34, warmth: 0.32, vignette: 0.12, skyTo: [0.9, 0.72, 0.48] },
  // whiteout: cold, flat, grey-blue
  blizzard: { ambient: [0.88, 0.92, 1.0], shadow: 0.15, sky: 0.6, saturation: -0.4, warmth: -0.5, vignette: 0.12, skyTo: [0.84, 0.88, 0.96] },
  // clear and bitter: cool, crisp, full shadows
  frost: { ambient: [0.94, 0.97, 1.0], shadow: 1.05, sky: 1, saturation: -0.1, warmth: -0.42, vignette: 0, skyTo: [0.86, 0.92, 1.0], contrast: 0.07 },
  // wet and cold: like rain, a little colder and brighter
  sleet: { ambient: [0.8, 0.84, 0.93], shadow: 0.22, sky: 0.65, saturation: -0.24, warmth: -0.34, vignette: 0.08 },
};

/** Wet kinds: the ground darkens, puddles gather, rain rings, rain particles. */
export const isWet = (k: WeatherKind): boolean => k === 'rain' || k === 'storm' || k === 'sleet';
/** Kinds that settle snow on the ground and drop flakes. */
export const isSnowy = (k: WeatherKind): boolean => k === 'snow' || k === 'blizzard';
/** Kinds cold enough that breath shows. */
export const isCold = (k: WeatherKind): boolean => k === 'snow' || k === 'blizzard' || k === 'frost' || k === 'sleet';

const GREY: RGB = [0.78, 0.8, 0.84];

/** The hour's light under a weather. Pure; every floor of docs/19 N2 still holds. */
export function weathered(day: DayLight, w: Weather): DayLight {
  if (w.kind === 'clear') return day;
  const s = SHIFT[w.kind], k = w.intensity;
  const lerp = (a: number, b: number): number => a + (b - a) * k;
  // the sun's warmth grades only when there is a sun to feel: heat at night is a warm, still dark
  const sunlit = day.setting === 'outdoor' && !!day.sun && !day.sun.moon;
  const gradeK = w.kind === 'heat' && !sunlit ? 0.35 : 1;
  const out: DayLight = {
    ...day,
    sky: mix(day.sky, s.skyTo ?? GREY, (1 - s.sky) * k + (s.skyTo && s.sky >= 1 ? 0.25 * k : 0)),
    skyStrength: day.skyStrength * lerp(1, Math.min(1, s.sky)),
    saturation: day.saturation + s.saturation * k * gradeK,
    warmth: day.warmth + s.warmth * k * gradeK,
    vignette: Math.max(0, day.vignette + s.vignette * k),
    contrast: (day.contrast ?? 0) + (s.contrast ?? 0) * k * gradeK,
    exposure: (day.exposure ?? 1) * lerp(1, s.exposure ?? 1) ** gradeK,
    atmosphere: { ...day.atmosphere },
    breath: day.breath || isCold(w.kind),
  };
  const wet = isWet(w.kind);
  const snowy = isSnowy(w.kind);
  if (day.setting === 'outdoor') {
    out.ambient = legible([day.ambient[0] * lerp(1, s.ambient[0]), day.ambient[1] * lerp(1, s.ambient[1]), day.ambient[2] * lerp(1, s.ambient[2])]);
    if (day.sun) out.sun = { ...day.sun, strength: Math.min(0.6, day.sun.strength * lerp(1, s.shadow)), length: day.sun.length * lerp(1, s.length ?? 1) };
    const dark = 1 - Math.min(1, luma(out.ambient));
    out.torch = Math.max(day.torch, 0.55 + dark * 0.9);
    out.lantern = Math.max(day.lantern, Math.max(0, Math.min(1, (dark - 0.12) * 2.6)));
    // rain and snow keep the insects and the dust down (and ground the petals and the pollen); fog and
    // drizzle bring the mist; snow replaces the falling leaves
    const a = out.atmosphere;
    if (wet || snowy || w.kind === 'sandstorm') { a.fireflies = false; a.motes = false; a.petals = false; a.pollen = false; }
    if (w.kind === 'fog' || (wet && w.intensity < 0.7)) a.mist = true;
    if (snowy || w.kind === 'sleet') a.leaves = false;
    if (w.kind === 'sandstorm' || w.kind === 'blizzard') { a.mist = false; a.leaves = false; a.glints = false; }
    if (w.kind === 'heat') { a.mist = false; a.rising = sunlit; if (sunlit) a.fireflies = false; }
    if (w.kind === 'frost') { a.glints = true; a.petals = false; a.pollen = false; a.fireflies = false; }
    if (w.kind === 'blizzard' || w.kind === 'sandstorm') a.petals = false;
  } else {
    if (wet) out.atmosphere.drips = true;
    if (w.kind === 'fog') out.atmosphere.mist = true;
  }
  return out;
}

/** How far the ground may be darkened (cloud shadow, wet earth) before an unlit square breaks a floor. */
export function darkBudget(ambient: RGB): number {
  const ratio = Math.max(AMBIENT_FLOOR / Math.min(ambient[0], ambient[1], ambient[2]), LUMA_FLOOR / luma(ambient));
  return Math.max(0, Math.min(DARK_CAP, 1 - ratio));
}

/** Everything the ground shader and the particles draw for one weather in one light (W4, docs/23). */
export interface WeatherLook {
  /** Cloud-shadow depth (0..budget) and how much sky is covered (0 clear .. 1 overcast). */
  cloud: number; cover: number;
  /** Wet ground: darkening share and puddle ripples (0..1); indoors only in the doorways. */
  wet: number;
  /** The darkest the shader may make the ground, multiplicatively (from `darkBudget`). */
  darkMax: number;
  /** Fog density 0..FOG_CAP and colour; how fast it drives with the wind (1 a drift, 4 a gale) and how far it streaks along it. */
  fog: number; fogColour: RGB; fogSpeed: number; fogStretch: number;
  /** Ground cover 0..SNOW_CAP (snow) or 0..SAND_CAP (sand drifts), its colour, how much it caps walls and canopies (0..1) and whether it gathers in drifts (0 an even dusting .. 1 drifts). */
  snow: number; snowColour: RGB; snowCaps: number; snowDrifts: number;
  /** Rime 0..FROST_CAP: crystals on open land and wall tops, ice on the water's edge. */
  frost: number;
  /** Heat shimmer 0..HEAT_CAP. */
  heat: number;
  /** Precipitation particles: rain streaks (and how long: 1 rain, ~0.6 sleet), snowflakes, and how hard the gale slants them (0..1). */
  rain: number; streak: number; snowfall: number; gale: number;
  /** Wind-blown grit (sand storm) 0..1. */
  grit: number; gritColour: RGB;
  /** Lightning: mean ms between flashes (0 = none). */
  flashEvery: number;
  /** The sky colour puddles reflect and lightning takes. */
  sky: RGB;
  wind: number;
  indoor: boolean;
}

const WHITE: RGB = [0.95, 0.97, 1.0];
const SAND: RGB = [0.88, 0.74, 0.52];

/** Mean ms between lightning strikes (0 = no storm); the storm schedule of core/audio/storm.ts runs on it. */
export function stormEvery(w: Weather): number {
  return w.kind === 'storm' ? Math.max(FLASH_GAP_MS * 1.5, 14000 - 6000 * w.intensity) : 0;
}

export function weatherLook(day: DayLight, w: Weather): WeatherLook {
  const k = w.intensity;
  const indoor = day.setting === 'indoor';
  // indoors the shader only wets the doorways, which daylight already brightens
  const darkMax = indoor ? Math.min(0.12, darkBudget(day.ambient)) : darkBudget(day.ambient);
  const kind = w.kind;
  const wet = isWet(kind);
  const cover = kind === 'clear' || kind === 'frost' ? 0.25 : kind === 'heat' ? 0.1 : kind === 'snow' ? 0.7 : kind === 'fog' ? 0.3 : 1;
  const fogBase = kind === 'fog' ? 0.25 + 0.75 * k
    : kind === 'sandstorm' ? 0.35 + 0.55 * k
    : kind === 'blizzard' ? 0.3 + 0.45 * k
    : kind === 'storm' ? 0.2 : kind === 'rain' ? 0.12 + 0.12 * k : kind === 'sleet' ? 0.14 + 0.1 * k
    : kind === 'snow' ? 0.07 : kind === 'overcast' ? 0.08 : 0;
  const fogColour: RGB = kind === 'sandstorm' ? mix(SAND, day.sky, 0.1) : kind === 'blizzard' ? mix([0.92, 0.95, 1.0], day.sky, 0.08) : mix([0.94, 0.95, 0.98], day.sky, 0.12);
  // ground cover: snow settles in drifts; a blizzard buries; sleet dusts but never drifts; a sand storm drifts sand
  const snowCover = indoor ? 0
    : kind === 'snow' ? Math.min(SNOW_CAP, (0.3 + 0.7 * k) * SNOW_CAP)
    : kind === 'blizzard' ? SNOW_CAP
    : kind === 'sleet' ? 0.4 * SNOW_CAP * (0.6 + 0.4 * k)
    : kind === 'sandstorm' ? Math.min(SAND_CAP, (0.35 + 0.65 * k) * SAND_CAP) : 0;
  const sunlit = !!day.sun && !day.sun.moon;
  const chill = indoor ? 0 : Math.max(day.chill ?? 0, kind === 'frost' ? 0.55 + 0.45 * k : 0);
  // docs/23 Y9: under the rock no cloud passes, no wind blows, the seep drips short and thin, and the
  // fog is a blue-green spore haze
  if (day.roofed) {
    const open = weatherLook({ ...day, roofed: false }, { ...w, wind: 0 });
    return {
      ...open,
      cloud: 0, cover: 0,
      rain: wet ? 0.12 + 0.2 * k : 0, streak: 0.45, gale: 0,
      fogColour: mix([0.62, 0.86, 0.8], day.sky, 0.2),
      wind: 0,
    };
  }
  // docs/23 Y10: under the Ashlands' pall a sandstorm is an ash storm (grey-black, the ash drifting
  // grey over the ground), the fog a yellow sulphur reek, and the rain falls dark
  if (day.pall && !indoor) {
    const open = weatherLook({ ...day, pall: false }, w);
    return {
      ...open,
      fogColour: kind === 'sandstorm' ? mix([0.3, 0.28, 0.27], day.sky, 0.1) : kind === 'fog' ? mix([0.8, 0.76, 0.5], day.sky, 0.15) : open.fogColour,
      snowColour: kind === 'sandstorm' ? [0.42, 0.4, 0.39] : open.snowColour,
      gritColour: mix([0.36, 0.34, 0.33], day.ambient, 0.25),
    };
  }
  return {
    cloud: indoor || kind === 'heat' ? 0 : Math.min(darkMax, (kind === 'clear' || kind === 'frost' ? 0.12 : 0.18 + 0.06 * k) * (day.sun?.moon ? 0.4 : 1)),
    cover,
    wet: wet ? (kind === 'sleet' ? 0.35 + 0.5 * k : 0.4 + 0.6 * k) : 0,
    darkMax,
    fog: Math.min(FOG_CAP, fogBase * (indoor ? 0.45 : 1) * FOG_CAP),
    fogColour,
    fogSpeed: kind === 'sandstorm' ? 4.5 : kind === 'blizzard' ? 3 : 1,
    fogStretch: kind === 'sandstorm' ? 2.4 : kind === 'blizzard' ? 1.8 : 1,
    snow: snowCover,
    snowColour: kind === 'sandstorm' ? [0.93, 0.8, 0.6] : WHITE,
    snowCaps: kind === 'sandstorm' ? 0.12 : kind === 'sleet' ? 0.7 : 1,
    snowDrifts: kind === 'sleet' ? 0 : 1,
    frost: Math.min(FROST_CAP, chill * FROST_CAP),
    heat: indoor || kind !== 'heat' ? 0 : Math.min(HEAT_CAP, (sunlit ? 0.45 + 0.55 * k : 0.15) * HEAT_CAP),
    rain: wet ? (kind === 'storm' ? 0.75 + 0.25 * k : kind === 'sleet' ? 0.3 + 0.45 * k : 0.25 + 0.6 * k) : 0,
    streak: kind === 'sleet' ? 0.6 : 1,
    snowfall: kind === 'snow' ? 0.3 + 0.7 * k : kind === 'blizzard' ? 1.2 + 0.6 * k : kind === 'sleet' ? 0.18 + 0.12 * k : 0,
    gale: kind === 'blizzard' ? 0.6 + 0.4 * k : kind === 'sleet' ? 0.25 : 0,
    grit: kind === 'sandstorm' ? 0.4 + 0.6 * k : 0,
    gritColour: mix([0.94, 0.8, 0.56], day.ambient, 0.3),
    flashEvery: stormEvery(w),
    sky: day.sky,
    wind: w.wind,
    indoor,
  };
}
